import {
    quoteAuthoringFingerprintFromScopeFingerprint,
    quoteRowsReviewedMatch,
    scopeFingerprintForAutoDraft,
    scopeFingerprintForQuoteAuthoring,
} from './auto-pipeline.js';
import { assessStage3WorkMethod, hasFreshWmsScopeProjection, isStage3StaleOnly } from './stage3-work-method';
import { isStage3StaleHardBlock } from './feature-flags';
import { assessQuotePricingReadiness } from './quote-pricing-readiness';
import { PROJECT_WORKFLOW_MODES } from '../services/project-workflow';
import { sha256Text } from '../quote-engine/canonical-json';
import { isBrainPipelineVersion, isBrainScope, isQuoteBrainQuoteData } from './quote-brain-project';

// A project envelope as read by this module. Every field is optional and
// untyped (`unknown`): projects arrive from Dexie / the API / AI output in both
// camelCase and snake_case shapes, and every read here is defensive (optional
// chaining / `??` / typeof guards). This mirrors the runtime contract, not a
// strict schema.
interface ProjectLike {
    status?: unknown;
    meta?: unknown;
    completedStages?: unknown;
    completed_stages?: unknown;
    currentStage?: unknown;
    current_stage?: unknown;
    quoteData?: unknown;
    quote_data?: unknown;
    quoteComplete?: unknown;
    quote_complete?: unknown;
    wms?: unknown;
    projectWMS?: unknown;
    project_wms?: unknown;
    projectStrategy?: unknown;
    project_strategy?: unknown;
    riskAnalysis?: unknown;
    risk_analysis?: unknown;
    photoAnalysis?: unknown;
    photo_analysis?: unknown;
    scope?: unknown;
    scopeGstInclusive?: unknown;
    scope_gst_inclusive?: unknown;
    formData?: unknown;
    form_data?: unknown;
    photos?: unknown;
    documents?: unknown;
    videos?: unknown;
    projectSummary?: unknown;
    project_summary?: unknown;
    restoredFromSyncFolder?: unknown;
    workflowMode?: unknown;
    workflow_mode?: unknown;
    projectWorkflowMode?: unknown;
    project_workflow_mode?: unknown;
    projectWMSGenerating?: unknown;
    project_wms_generating?: unknown;
    isScopeMorphing?: unknown;
    is_scope_morphing?: unknown;
    pipelineVersion?: unknown;
    pipeline_version?: unknown;
}

type ProjectRecord = ProjectLike & Record<string, unknown>;

export const QUOTE_COMPLETION_CONTRACT_VERSION = 1;
export const STAGE5_PRESENTATION_CONTRACT_VERSION = 1;

const COMPLETED_QUOTE_STATUSES = new Set([
    'quoted',
    'ready_for_builder_review',
    'sent',
    'won',
    'lost',
]);

export function normalizeCompletedStages(stagesRaw: unknown): Set<number> {
    let values: unknown[] = [];

    if (stagesRaw instanceof Set) {
        values = Array.from(stagesRaw);
    } else if (Array.isArray(stagesRaw)) {
        values = stagesRaw;
    } else if (typeof stagesRaw === 'string') {
        const trimmed = stagesRaw.trim();
        if (trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);
                values = Array.isArray(parsed) ? parsed : [];
            } catch {
                values = [];
            }
        } else if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            values = trimmed.slice(1, -1).split(',');
        } else if (trimmed) {
            values = trimmed.split(',');
        }
    }

    return new Set(
        values
            .map(stage => Number(String(stage).trim().replace(/^"|"$/g, '')))
            .filter(stage => Number.isFinite(stage))
    );
}

export function canInspectorNotesAutoRefreshPipeline({
    stage = 1,
    completedStages = [],
}: {
    stage?: unknown;
    completedStages?: unknown;
} = {}): boolean {
    const currentStage = Number(stage) || 1;
    const completed = normalizeCompletedStages(completedStages);

    if (currentStage >= 3) return false;
    return ![2, 3, 4, 5].some(stageNumber => completed.has(stageNumber));
}

function projectMeta(project: ProjectLike | null | undefined): Record<string, unknown> {
    return project?.meta && typeof project.meta === 'object'
        ? project.meta as Record<string, unknown>
        : {};
}

function objectRecord(value: unknown): Record<string, unknown> | null {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

/**
 * A Stage 5 snapshot is regeneration authority only after an explicit builder
 * scope edit. Merely being AI-authored/generated is not review evidence and
 * must not replace the priced Stage 4 rows on a later Regen Quote click.
 */
export function isBuilderEditedQuoteScopeSnapshot(snapshot: unknown): boolean {
    const record = objectRecord(snapshot);
    if (!record) return false;
    const meta = objectRecord(record.meta);
    if (record.scopeEditedBy === 'user' || meta?.scopeEditedBy === 'user') return true;
    return Array.isArray(record.scope) && record.scope.some((item) => {
        const row = objectRecord(item);
        return row?.manuallyEdited === true
            || row?.scopeEditedBy === 'user'
            || row?.editedBy === 'user';
    });
}

/**
 * True when a Stage 5 snapshot was bound to a Stage 4 scope that has changed
 * since: its presentation receipt names a different Stage 4 than the live one.
 * Stage 4 then changed after the Stage 5 edit (a Regen WMS, or Stage 4 edits),
 * so the Stage 5 rows are not the builder's word on the current work. On
 * 2026-10-12 Regenerate Quote on such a quote wrote the 11 October Stage 5
 * item back over a regenerated Stage 4 with no warning (F46). A snapshot with
 * no receipt (older quotes) is never called superseded.
 */
export function isStage5SnapshotSupersededByStage4(snapshot: unknown, liveScope: unknown): boolean {
    const record = objectRecord(snapshot);
    const receipt = objectRecord(objectRecord(record?.meta)?.stage5Presentation);
    const scope = Array.isArray(liveScope) ? liveScope : [];
    if (!receipt || receipt.available !== true || scope.length === 0) return false;
    let stage4Changed = false;
    if (typeof receipt.sourceAuthoringFingerprint === 'string' && receipt.sourceAuthoringFingerprint.length > 0) {
        stage4Changed = receipt.sourceAuthoringFingerprint !== scopeFingerprintForQuoteAuthoring(scope);
    } else if (
        typeof receipt.sourceAuthoringFingerprintHash === 'string'
        && /^sha256:[0-9a-f]{64}$/.test(receipt.sourceAuthoringFingerprintHash)
    ) {
        stage4Changed = receipt.sourceAuthoringFingerprintHash !== sha256Text(scopeFingerprintForQuoteAuthoring(scope));
    }
    // The authoring fingerprint also moves on a cost recompute or a copied
    // description field, so the rows' own words and prices must differ too.
    return stage4Changed && rowsContentKey(record?.scope) !== rowsContentKey(scope);
}

// What a builder sees of the rows: id, title, price and lines, in any order.
function rowsContentKey(rows: unknown): string {
    const words = (value: unknown) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const price = (value: unknown) => {
        const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
        return Number.isFinite(parsed) && String(value ?? '').trim() !== '' ? parsed.toFixed(2) : '';
    };
    return JSON.stringify((Array.isArray(rows) ? rows : []).map((item) => {
        const row = objectRecord(item) || {};
        const lines = Array.isArray(row.inclusions)
            ? row.inclusions.map(entry => words(typeof entry === 'string' ? entry : objectRecord(entry)?.text))
            : [];
        return JSON.stringify([String(row.id ?? ''), words(row.title), price(row.amount), lines]);
    }).sort());
}

/**
 * Which Stage 5 snapshot a Regenerate Quote may author from. A builder-edited
 * snapshot is the next authoring input (and is written back to Stage 4 first)
 * only while it still belongs to the live Stage 4 scope. Otherwise the
 * regeneration authors from Stage 4 and leaves Stage 4 alone (F46); Save
 * first writes the Stage 5 items into Stage 4 when they should win.
 */
export function resolveStage5RegenerationAuthority({
    suppliedSnapshot,
    liveScope,
}: {
    suppliedSnapshot: unknown;
    liveScope: unknown;
}): { builderEditedSnapshot: Record<string, unknown> | null; supersededByStage4: boolean } {
    if (!isBuilderEditedQuoteScopeSnapshot(suppliedSnapshot)) {
        return { builderEditedSnapshot: null, supersededByStage4: false };
    }
    const supersededByStage4 = isStage5SnapshotSupersededByStage4(suppliedSnapshot, liveScope);
    return {
        builderEditedSnapshot: supersededByStage4 ? null : objectRecord(suppliedSnapshot),
        supersededByStage4,
    };
}

function projectQuoteData(project: ProjectLike | null | undefined): Record<string, unknown> | null {
    return objectRecord(project?.quoteData ?? project?.quote_data);
}

function completionMeta(project: ProjectLike | null | undefined): Record<string, unknown> {
    const quoteData = projectQuoteData(project);
    const meta = objectRecord(quoteData?.meta);
    return objectRecord(meta?.completion) || {};
}

function workingScope(project: ProjectLike | null | undefined): unknown[] {
    return Array.isArray(project?.scope) ? project.scope : [];
}

function generatedScope(project: ProjectLike | null | undefined): unknown[] {
    const quoteData = projectQuoteData(project);
    return Array.isArray(quoteData?.scope) ? quoteData.scope : [];
}

export function quoteCompletionScopeFingerprint(scope: unknown): string | null {
    if (!Array.isArray(scope) || scope.length === 0) return null;
    return scopeFingerprintForAutoDraft(scope);
}

/**
 * Mark a quote as a durable Stage 5 presentation draft without claiming that
 * it is complete, reviewed, issued or sent. This is the saved peer-view receipt
 * used to restore Stage 5 after reload for deterministic Stage 4 projections.
 */
export function bindStage5PresentationToScope(
    quoteDataInput: unknown,
    scopeInput: unknown,
): Record<string, unknown> {
    const quoteData = objectRecord(quoteDataInput) || {};
    const scope = Array.isArray(scopeInput) ? scopeInput : [];
    const sourceScopeFingerprint = quoteCompletionScopeFingerprint(scope);
    const sourceAuthoringFingerprint = scopeFingerprintForQuoteAuthoring(scope);
    const meta = objectRecord(quoteData.meta) || {};
    return {
        ...quoteData,
        meta: {
            ...meta,
            stage5Presentation: {
                contractVersion: STAGE5_PRESENTATION_CONTRACT_VERSION,
                available: true,
                sourceScopeFingerprint,
                sourceAuthoringFingerprint,
            },
        },
    };
}

/**
 * True when a saved project has a renderable Stage 5 draft bound to its current
 * authoritative scope. This deliberately does not satisfy completed-quote or
 * issued-artifact contracts.
 */
export function projectHasStage5Presentation(project: ProjectLike | null | undefined): boolean {
    const quoteData = projectQuoteData(project);
    const meta = objectRecord(quoteData?.meta);
    const receipt = objectRecord(meta?.stage5Presentation);
    if (
        receipt?.available !== true
        || Number(receipt?.contractVersion) !== STAGE5_PRESENTATION_CONTRACT_VERSION
        || generatedScope(project).length === 0
        || workingScope(project).length === 0
    ) {
        return false;
    }
    const currentAuthoringFingerprint = scopeFingerprintForQuoteAuthoring(workingScope(project));
    if (
        typeof receipt.sourceAuthoringFingerprint === 'string'
        && receipt.sourceAuthoringFingerprint.length > 0
    ) {
        return receipt.sourceAuthoringFingerprint === currentAuthoringFingerprint;
    }
    if (
        typeof receipt.sourceAuthoringFingerprintHash === 'string'
        && /^sha256:[0-9a-f]{64}$/.test(receipt.sourceAuthoringFingerprintHash)
    ) {
        return receipt.sourceAuthoringFingerprintHash === sha256Text(currentAuthoringFingerprint);
    }
    const currentScopeFingerprint = quoteCompletionScopeFingerprint(workingScope(project));
    if (typeof receipt.sourceScopeFingerprint === 'string' && receipt.sourceScopeFingerprint.length > 0) {
        return receipt.sourceScopeFingerprint === currentScopeFingerprint;
    }
    return typeof currentScopeFingerprint === 'string'
        && typeof receipt.sourceScopeFingerprintHash === 'string'
        && /^sha256:[0-9a-f]{64}$/.test(receipt.sourceScopeFingerprintHash)
        && receipt.sourceScopeFingerprintHash === sha256Text(currentScopeFingerprint);
}

/**
 * Whether the quote describes these Stage 4 items: written for them, or
 * reviewed against them in Stage 5. The browser records this on every save of
 * a one-set quote (meta.rowsMirrorQuoteCurrent), because the server's project
 * list cannot recompute it and the two copies are now always equal (review W2,
 * 2026-10-23).
 */
export function quoteRowsCurrentForScope(scope: unknown, quoteData: unknown): boolean {
    return quoteFingerprintMatchesCurrentScope({ scope, quoteData } as ProjectLike);
}

function quoteFingerprintMatchesCurrentScope(project: ProjectLike | null | undefined): boolean {
    const currentFingerprint = quoteCompletionScopeFingerprint(workingScope(project));
    if (!currentFingerprint) return false;
    const currentAuthoringFingerprint = scopeFingerprintForQuoteAuthoring(workingScope(project));

    // The builder's Stage 5 review of exactly the current rows. Under the
    // one-set rule the quote's rows always equal Stage 4's, so this reads the
    // recorded reviewed rows instead of that equality (auto-pipeline.js).
    const reviewedScopeMatches = quoteRowsReviewedMatch(projectQuoteData(project), workingScope(project));

    // New quote authors stamp the commercial/copy inputs separately from the
    // broad media-sensitive scope receipt. Completion/navigation freshness uses
    // the narrow receipt; sourceScopeFingerprint remains the immutable artifact
    // and concurrency proof and is deliberately not rewritten here.
    const quoteMeta = objectRecord(projectQuoteData(project)?.meta) || {};
    const persistedAuthoringFingerprint = completionMeta(project).sourceAuthoringFingerprint
        ?? quoteMeta.sourceAuthoringFingerprint;
    if (typeof persistedAuthoringFingerprint === 'string' && persistedAuthoringFingerprint.length > 0) {
        return persistedAuthoringFingerprint === currentAuthoringFingerprint
            || reviewedScopeMatches;
    }

    // Existing quotes only have the broad JSON fingerprint. Project that
    // receipt onto the same authoring fields so a photo assignment can preserve
    // Stage 5 completion without blessing a later scope-copy or price edit.
    const persistedFingerprint = completionMeta(project).sourceScopeFingerprint
        ?? quoteMeta.sourceScopeFingerprint;
    if (typeof persistedFingerprint === 'string' && persistedFingerprint.length > 0) {
        if (quoteAuthoringFingerprintFromScopeFingerprint(persistedFingerprint) === currentAuthoringFingerprint) {
            return true;
        }
        // Older attended Stage 5 saves wrote the reviewed quote rows back to
        // the authoritative scope but did not refresh the source receipt. An
        // exact quote/live canonical match is safe compatibility evidence;
        // any later Stage 4 edit still invalidates the quote.
        return reviewedScopeMatches;
    }

    // Conservative compatibility for pre-contract quotes. Only an exact
    // canonical-scope match is accepted; once bound, every subsequent edit is
    // checked against the persisted source fingerprint instead.
    return reviewedScopeMatches;
}

function numericMoney(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !value.trim()) return null;
    const parsed = Number(value.replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * v5 portable snapshots stored the final subtotal/GST/total beside canonical
 * top-level scope but did not duplicate that scope under quoteData. The three
 * reconciled monetary values are the historical generation proof; a header,
 * status or Stage-5 flag by itself is deliberately insufficient.
 */
export function hasLegacyGeneratedQuoteTotals(project: ProjectLike | null | undefined): boolean {
    const quoteData = projectQuoteData(project);
    if (!quoteData || generatedScope(project).length > 0) return false;
    const totals = objectRecord(quoteData.totals);
    const subtotal = numericMoney(quoteData.subtotal ?? totals?.subtotal ?? totals?.subtotalExGst);
    const gst = numericMoney(quoteData.gst ?? totals?.gst ?? totals?.gstAmount);
    const total = numericMoney(quoteData.total ?? totals?.total ?? totals?.totalIncGst);
    if (subtotal == null || gst == null || total == null || subtotal < 0 || gst < 0 || total <= 0) {
        return false;
    }
    return Math.abs((subtotal + gst) - total) <= 0.02;
}

type QuoteBindingResult = {
    project: ProjectRecord;
    changed: boolean;
    bound: boolean;
    migratedLegacyTotals: boolean;
    fingerprint: string | null;
};

/**
 * Bind a freshly generated quote to the authoritative working scope. This is
 * intentionally immutable so callers can persist the returned envelope with
 * one Dexie/API write. `allowLegacyTotals` is reserved for the recognised v5
 * portable snapshot migration above.
 */
export function bindGeneratedQuoteToCurrentScope(
    project: ProjectLike | null | undefined,
    {
        readyAt,
        allowLegacyTotals = false,
    }: { readyAt?: unknown; allowLegacyTotals?: boolean } = {},
): QuoteBindingResult {
    const original = (project || {}) as ProjectRecord;
    const scope = workingScope(project);
    const quoteData = projectQuoteData(project);
    const fingerprint = quoteCompletionScopeFingerprint(scope);
    if (!quoteData || !fingerprint) {
        return { project: original, changed: false, bound: false, migratedLegacyTotals: false, fingerprint };
    }

    let nextQuoteData = quoteData;
    let migratedLegacyTotals = false;
    if (generatedScope(project).length === 0) {
        if (!allowLegacyTotals || !hasLegacyGeneratedQuoteTotals(project)) {
            return { project: original, changed: false, bound: false, migratedLegacyTotals: false, fingerprint };
        }
        nextQuoteData = { ...nextQuoteData, scope: [...scope] };
        migratedLegacyTotals = true;
    }

    const priorMeta = objectRecord(nextQuoteData.meta) || {};
    const priorCompletion = objectRecord(priorMeta.completion) || {};
    const readyForReviewAt = typeof readyAt === 'string' && Number.isFinite(Date.parse(readyAt))
        ? readyAt
        : priorCompletion.readyForReviewAt;
    const nextCompletion = {
        ...priorCompletion,
        contractVersion: QUOTE_COMPLETION_CONTRACT_VERSION,
        sourceScopeFingerprint: fingerprint,
        ...(readyForReviewAt ? { readyForReviewAt } : {}),
        ...(migratedLegacyTotals ? { migratedLegacyTotals: true } : {}),
    };
    const unchanged = !migratedLegacyTotals
        && JSON.stringify(priorCompletion) === JSON.stringify(nextCompletion);
    if (unchanged) {
        return { project: original, changed: false, bound: true, migratedLegacyTotals: false, fingerprint };
    }

    nextQuoteData = {
        ...nextQuoteData,
        meta: { ...priorMeta, completion: nextCompletion },
    };
    const usesSnakeCase = project?.quoteData === undefined && project?.quote_data !== undefined;
    return {
        project: {
            ...original,
            ...(usesSnakeCase ? { quote_data: nextQuoteData } : { quoteData: nextQuoteData }),
        },
        changed: true,
        bound: true,
        migratedLegacyTotals,
        fingerprint,
    };
}

/**
 * One-time, conservative migration used by restore/queue paths. Modern
 * unbound quotes are bound only when generated and working scopes already
 * match. The v5 totals-only shape is migrated only with reconciled totals.
 */
export function migrateLegacyCompletedQuoteEnvelope(
    project: ProjectLike | null | undefined,
): QuoteBindingResult {
    const original = (project || {}) as ProjectRecord;
    if (!hasCompletedQuoteWorkflowEvidence(original)) {
        return { project: original, changed: false, bound: false, migratedLegacyTotals: false, fingerprint: null };
    }
    if (typeof completionMeta(project).sourceScopeFingerprint === 'string') {
        return {
            project: original,
            changed: false,
            bound: quoteFingerprintMatchesCurrentScope(project),
            migratedLegacyTotals: false,
            fingerprint: quoteCompletionScopeFingerprint(workingScope(project)),
        };
    }
    if (generatedScope(project).length > 0 && !quoteFingerprintMatchesCurrentScope(project)) {
        return {
            project: original,
            changed: false,
            bound: false,
            migratedLegacyTotals: false,
            fingerprint: quoteCompletionScopeFingerprint(workingScope(project)),
        };
    }
    return bindGeneratedQuoteToCurrentScope(original, {
        allowLegacyTotals: hasLegacyGeneratedQuoteTotals(project),
    });
}

export function hasCompleteGeneratedQuoteStructure(project: ProjectLike | null | undefined): boolean {
    const quoteData = projectQuoteData(project);
    const quoteMeta = objectRecord(quoteData?.meta);
    if (quoteMeta?.generatedBy === 'deterministic-stage5-draft') return false;
    return workingScope(project).length > 0
        && generatedScope(project).length > 0
        && quoteFingerprintMatchesCurrentScope(project);
}

function hasCompletedQuoteWorkflowEvidence(project: ProjectLike): boolean {
    const meta = projectMeta(project);
    const completed = normalizeCompletedStages(
        project.completedStages
        ?? project.completed_stages
        ?? meta.completedStages
        ?? meta.completed_stages,
    );
    const stage = Number(
        project.currentStage
        ?? project.current_stage
        ?? meta.currentStage
        ?? meta.current_stage,
    );
    const status = String(project.status ?? meta.status ?? '').trim().toLowerCase();
    return stage >= 5 || completed.has(5) || COMPLETED_QUOTE_STATUSES.has(status);
}

export function projectHasCompletedQuote(project: ProjectLike | null | undefined): boolean {
    if (!project) return false;

    // Lean project summaries deliberately omit quoteData. Their producer must
    // calculate this boolean from the full row before discarding the payload;
    // never infer completion from a stage flag once structural evidence is gone.
    const summaryCompletion = project.quoteComplete ?? project.quote_complete;
    if (typeof summaryCompletion === 'boolean') return summaryCompletion;

    if (!hasCompletedQuoteWorkflowEvidence(project) || workingScope(project).length === 0) {
        return false;
    }
    return hasCompleteGeneratedQuoteStructure(project)
        || hasLegacyGeneratedQuoteTotals(project);
}

/**
 * A quote the pipeline drafted that no human has opened and saved yet.
 *
 * `completeQuote` stamps `aiDrafted: true` + `reviewedAt: null` on every quote
 * that crosses the Stage 5 completion boundary, because its only production
 * caller is the pipeline's terminal writer. The quote editor's save handler
 * stamps `reviewedAt` when the operator actually looks at it. Without this the
 * Run Sheet showed AI output as "Ready to send", identical to a quote the
 * operator wrote and checked himself, while the batch queue promised the
 * opposite ("results stay unsent for human fine-tuning").
 *
 * Deliberately conservative: a quote with no completion receipt at all (every
 * quote produced before this shipped) is NOT reported as unreviewed, so the
 * marker never appears retroactively on historical work.
 */
export function quoteAwaitsHumanReview(project: ProjectLike | null | undefined): boolean {
    if (!project) return false;
    const quoteData: any = (project as any).quoteData ?? (project as any).quote_data;
    const completion = quoteData?.meta?.completion;
    if (!completion || typeof completion !== 'object') return false;
    return completion.aiDrafted === true && !completion.reviewedAt;
}

function hasText(value: unknown): boolean {
    return typeof value === 'string' && value.replace(/<[^>]*>/g, ' ').trim().length > 0;
}

function hasQuoteData(project: ProjectLike | null | undefined): boolean {
    // A generated scope (or the recognised reconciled legacy totals shape) is
    // quote evidence. Header/default shells are not: those can be persisted
    // before generation and previously unlocked Stage 5 prematurely.
    return generatedScope(project).length > 0 || hasLegacyGeneratedQuoteTotals(project);
}

/**
 * 5C / O-F10. A prior quote that can be opened read-only while a replacement is being authored: a quote OBJECT whose own `scope` array is non-empty.
 * A header-only or default shell, an array, an empty scope or nothing at all is not one. The Stage 4 Next (`canGoNext`) and the Stage 5 pill
 * (`canNavigateQuoteStage`, current 4 to target 5) both read THIS rule, so the two controls cannot give different answers for the same view.
 */
export function hasUsablePriorQuoteSnapshot(quoteData: unknown): boolean {
    if (!quoteData || typeof quoteData !== 'object' || Array.isArray(quoteData)) return false;
    const scope = (quoteData as { scope?: unknown }).scope;
    return Array.isArray(scope) && scope.length > 0;
}

function hasStructuredWms(project: ProjectLike | null | undefined): boolean {
    const wms = project?.wms || project?.projectWMS || project?.project_wms;
    const w = wms as Record<string, unknown> | null | undefined;
    return !!(wms && typeof wms === 'object' && (w!.structured || w!.overall_programme || w!.overallProgramme));
}

function stage3MethodReady(project: ProjectLike | null | undefined): boolean {
    const wms = project?.wms || project?.projectWMS || project?.project_wms;
    const assessment = assessStage3WorkMethod({
        wms,
        scope: project?.scope,
        strategy: project?.projectStrategy || project?.project_strategy,
    });
    // Soft mode (default, v5.47.3): a COMPLETE method that is merely stale
    // stays navigable/complete — Stage 4 pricing edits legitimately drift the
    // scope from the method stamp, and refusing navigation here read as
    // "stuck at Stage 3". Missing/fallback/incomplete methods still refuse.
    return assessment.ready
        || (!isStage3StaleHardBlock() && isStage3StaleOnly(assessment));
}

function hasReportArray(value: unknown): boolean {
    return Array.isArray(value) && value.length > 0;
}

/** A Risk report with content (5B / O-F11): the evidence that Stage 2 happened. A Work Method, scope or a shell object is not. */
export function hasRiskAnalysisPayload(project: ProjectLike | null | undefined): boolean {
    const analysis = project?.riskAnalysis || project?.risk_analysis;
    if (!analysis || typeof analysis !== 'object') return false;
    const a = analysis as Record<string, unknown>;
    if (hasText(a.executive_summary || a.executiveSummary || a.summary)) return true;
    if (hasText(a.risk_level || a.riskLevel || a.confidence_label)) return true;
    const confidence = a.confidence_score ?? a.confidenceScore;
    if (confidence !== null && confidence !== undefined && confidence !== '' && Number.isFinite(Number(confidence))) return true;
    return hasReportArray(a.detected_risks)
        || hasReportArray(a.riskItems)
        || hasReportArray(a.issues)
        || hasReportArray(a.detected_standards)
        || hasReportArray(a.suggested_exclusions);
}

function hasRiskStagePayload(project: ProjectLike | null | undefined): boolean {
    return !!(hasRiskAnalysisPayload(project) || project?.photoAnalysis || project?.photo_analysis);
}

function hasScopeItems(project: ProjectLike | null | undefined): boolean {
    return Array.isArray(project?.scope) && (project!.scope as unknown[]).length > 0;
}

function projectWorkflowModeOf(project: ProjectLike | null | undefined): unknown {
    return project?.workflowMode
        ?? project?.workflow_mode
        ?? project?.projectWorkflowMode
        ?? project?.project_workflow_mode;
}

function isStrictFullAiWorkflow(project: ProjectLike | null | undefined): boolean {
    return projectWorkflowModeOf(project) === PROJECT_WORKFLOW_MODES.FULL_AI;
}

/**
 * 5B / O-F11. The project went from the Summary to the Scope Builder without Risk or Method: either its workflow mode IS the
 * Fast-Track manual-scope route, or it carries the re-quote shortcut's provenance (`formData.requoteContext.isRequote`, written by the
 * shortcut whatever the project's workflow mode, Full AI included). Neither fact says Risk or Method happened.
 */
function skipsRiskAndMethod(project: ProjectLike | null | undefined): boolean {
    if (projectWorkflowModeOf(project) === PROJECT_WORKFLOW_MODES.FAST_TRACK_MANUAL_SCOPE) return true;
    return hasRequoteProvenance(project);
}

function hasRequoteProvenance(project: ProjectLike | null | undefined): boolean {
    const formData = objectRecord(project?.formData ?? project?.form_data);
    const context = objectRecord(formData?.requoteContext);
    return context?.isRequote === true;
}

/**
 * The Quote Brain wrote this job's quote (docs/quote-brain/DESIGN.md 2.11): the record is stamped 'quote-brain-v1', its rows are
 * the brain's, or its quote carries the brain's run record. Read from the project's own content, so every caller of
 * inferCompletedStages answers the same way without the tab's loaded-project registry.
 */
export function isQuoteBrainQuote(project: ProjectLike | null | undefined): boolean {
    if (!project) return false;
    if (isBrainPipelineVersion(project.pipelineVersion ?? project.pipeline_version)) return true;
    if (isBrainScope(project.scope)) return true;
    return isQuoteBrainQuoteData(project.quoteData ?? project.quote_data);
}

function isStage3ProjectionBusy(project: ProjectLike | null | undefined): boolean {
    return project?.projectWMSGenerating === true
        || project?.project_wms_generating === true
        || project?.isScopeMorphing === true
        || project?.is_scope_morphing === true;
}

// v5.19.17 (Peter 2026-05-05): re-analysis must NOT block navigation to
// stages whose data already exists. Pre-fix, hitting RE-ANALYSE while on a
// fully-quoted project locked the user on Stage 2's AI Stream view —
// METHOD / BUILDER / QUOTE were green-ticked from the previous run but
// the keyboard shortcuts and stage-strip clicks all silently no-oped, and
// the NEXT button was greyed out as "Analysing".
//
// New rule: during re-analysis, allow navigation to ANY stage whose data
// already exists (hasStructuredWms / hasScopeItems / hasQuoteData). Only
// block stages where the analysis would PRODUCE the data that hasn't
// landed yet (e.g. risk-analysis is still in flight on a fresh project
// with no prior data — blocking >=3 makes sense there).
export function hasQuoteStageReadiness(
    project: ProjectLike = {},
    targetStage?: unknown,
    options: { isAnalyzing?: unknown } = {},
): boolean {
    const target = Number(targetStage);
    if (!Number.isFinite(target)) return false;
    if (target <= 1) return true;
    // A completed quote implies every authoring stage is reopenable. This
    // catches saved/imported projects whose currentStage is stale but Stage 5
    // quote data exists, so navigation and the assistant do not pretend Stage
    // 2/3/4 are unavailable.
    if (projectHasCompletedQuote(project)) {
        // A completed quote stays reopenable. v5.47.3: the target-4 refusal on
        // a stale/incomplete Method was dropped — refusing NAVIGATION never
        // helped the operator fix the Method; it just made the stage strip
        // silently no-op ("the app is stuck"). Authoring gates live at the
        // NEXT/dialog layer (use-stage-navigation), not here.
        return target <= 5;
    }
    if (options.isAnalyzing && target >= 3 && !hasStructuredWms(project) && !hasScopeItems(project)) {
        return false;
    }
    if (target === 2) return !!options.isAnalyzing || hasRiskStagePayload(project) || hasStructuredWms(project) || hasScopeItems(project);
    if (target === 3) return hasRiskAnalysisPayload(project) || hasStructuredWms(project) || hasScopeItems(project);
    if (target === 4) {
        if (isStrictFullAiWorkflow(project)) {
            if (isStage3ProjectionBusy(project)) return false;
            const wms = project.wms || project.projectWMS || project.project_wms;
            const completed = normalizeCompletedStages(
                project.completedStages
                ?? project.completed_stages
                ?? projectMeta(project).completedStages
                ?? projectMeta(project).completed_stages,
            );
            return hasQuoteData(project)
                || hasFreshWmsScopeProjection(wms, project.scope)
                || projectHasStage5Presentation(project)
                || (hasScopeItems(project) && (
                    completed.has(3)
                    || completed.has(4)
                    || completed.has(5)
                    || isBuilderEditedQuoteScopeSnapshot(project)
                    // 5B: a re-quote's scope reaches Scope Builder by its own route. It used to be let in by a recorded "Method
                    // done" that never happened (the shortcut skips Method), which the completion record no longer claims.
                    || hasRequoteProvenance(project)
                ));
        }
        // v5.47.3: navigation shows what exists — a project with scope rows is
        // inspectable at Stage 4 even when its Method is fallback/incomplete
        // (common for queue-prepared drafts). The Method-quality gates remain
        // at the Stage 3 NEXT/dialog boundary in use-stage-navigation.
        return hasScopeItems(project);
    }
    // Stage 5 is a second view over Stage 4's authored scope. A saved AI quote
    // payload can open directly, but a live/manual Stage 4 scope is also enough:
    // App builds a deterministic Stage 5 draft from the same rows on entry.
    if (target === 5) {
        if (isStrictFullAiWorkflow(project)) {
            if (isStage3ProjectionBusy(project)) return false;
            // Upstream Full-AI navigation still needs the final Stage 3
            // projection. The current Stage 4 → 5 peer exception is applied in
            // canNavigateQuoteStage, where currentStage is still available.
            const wms = project.wms || project.projectWMS || project.project_wms;
            return hasQuoteData(project) || hasFreshWmsScopeProjection(wms, project.scope);
        }
        if (hasScopeItems(project)) {
            const pricingReadiness = assessQuotePricingReadiness({
                scope: project.scope,
                projectWMS: project.wms || project.projectWMS || project.project_wms,
                isGstInclusive: (project.scopeGstInclusive ?? project.scope_gst_inclusive) !== false,
            });
            if (
                pricingReadiness.pendingInclusionPricingReview
                || pricingReadiness.overAllocatedAllowance
            ) return false;
        }
        // A non-empty object is not quote evidence: persistence can create a
        // header/default shell before Stage 5 generation has produced any
        // client-facing scope. Only a structurally bound generated quote (or
        // the live Stage 4 scope that Stage 5 can deterministically project)
        // makes the final stage navigable.
        return hasQuoteData(project) || hasScopeItems(project);
    }
    return false;
}

export function canNavigateQuoteStage({
    currentStage = 1,
    targetStage = 1,
    isAnalyzing = false,
    ...project
}: ProjectLike & { targetStage?: unknown; isAnalyzing?: unknown } = {}): boolean {
    const current = Number(currentStage) || 1;
    const target = Number(targetStage) || 1;
    // Backwards: always allow (user revisiting earlier completed stages).
    if (target <= current) return true;
    // Stage 4 and Stage 5 are peer views over the same live authoring package.
    // A normal Stage 4 edit may intentionally stale the earlier WMS/costing
    // receipts; that finding remains visible inside Stage 5 and at its output
    // actions, but cannot hide the view needed to review/fix it. Keep only the
    // active Stage 3 projection owner as a narrow paint-race guard.
    if (current === 4 && target === 5) {
        // 5C / O-F10: while a replacement runs, a USABLE prior quote can still be viewed (the same rule the Stage 4 Next applies); with none, the
        // pill waits as before. This grants access to the old quote only: it opens through the same path as Next and starts nothing.
        if (isStage3ProjectionBusy(project)
            && !(hasScopeItems(project) && hasUsablePriorQuoteSnapshot(project.quoteData ?? project.quote_data))) return false;
        return hasQuoteData(project) || hasScopeItems(project);
    }
    // Forwards: gate on data readiness for the target stage. The previous
    // blanket `if (isAnalyzing && target >= 3) return false;` made re-analysis
    // unusable on a fully-quoted project; readiness already encodes the
    // correct rule (Stage 3 needs riskAnalysis, Stage 4 needs scope, etc).
    return hasQuoteStageReadiness(project, target, { isAnalyzing });
}

export function resolveHistoryQuoteStageTarget({
    currentStage = 1,
    targetStage = 1,
    canNavigateToStage = () => true,
}: {
    currentStage?: unknown;
    targetStage?: unknown;
    canNavigateToStage?: (target: number) => unknown;
} = {}): number {
    const currentRaw = Number(currentStage);
    const targetRaw = Number(targetStage);
    const current = Number.isFinite(currentRaw)
        ? Math.max(1, Math.min(5, Math.trunc(currentRaw)))
        : 1;
    const target = Number.isFinite(targetRaw)
        ? Math.max(1, Math.min(5, Math.trunc(targetRaw)))
        : current;

    if (target <= current) return target;
    return canNavigateToStage(target) ? target : current;
}

/**
 * The view of a browser-history entry (4C / C06): the page and the stage.
 * Stage 1 has one view since the Project Summary moved into its Job cockpit, so
 * `summary` is always written false. It stays in the entry because entries
 * written before the merge carry it, and a stored `true` is still read (as
 * Stage 1 with the Summary tab, see resolveHistorySummaryTarget).
 */
export function historyViewState({ page, stage }: {
    page: string;
    stage: number;
}): { page: string; stage: number; summary: boolean } {
    return { page, stage, summary: false };
}

/** True when the entry the browser is already on records exactly this view, so pushing it again would add a duplicate. */
export function historyEntryRecordsView(
    entry: unknown,
    view: { page: string; stage: number; summary: boolean },
): boolean {
    if (!entry || typeof entry !== 'object') return false;
    const record = entry as { page?: unknown; stage?: unknown; summary?: unknown };
    // A legacy entry has no `summary`: it records Data Entry for stage 1, which is how it has always been read.
    return record.page === view.page
        && Number(record.stage) === view.stage
        && (record.summary === true) === view.summary;
}

/**
 * Which Stage 1 cockpit tab a history entry asks for once it has been resolved against what is on screen.
 *   true   show the Summary tab (an entry written while the old Project Summary page was open)
 *   false  leave the tab alone (or any stage other than 1)
 *   null   the entry does not say (a legacy { page, stage } entry written before the Summary was recorded): leave the view alone
 *
 * Absent-Summary fallback, defined here and nowhere else: an entry that names the Summary while there is no Summary and none
 * being produced (a project switched in between, a reload that kept the entry) leaves the tab alone, instead of opening
 * an empty Summary tab: Stage 1 is where a Summary is produced.
 */
export function resolveHistorySummaryTarget({
    resolvedStage = 1,
    entrySummary,
    canShowSummary = () => true,
}: {
    resolvedStage?: unknown;
    entrySummary?: unknown;
    canShowSummary?: () => unknown;
} = {}): boolean | null {
    if (Number(resolvedStage) !== 1) return false;
    if (typeof entrySummary !== 'boolean') return null;
    if (!entrySummary) return false;
    return canShowSummary() ? true : false;
}

export function inferCompletedStages(project: ProjectLike = {}): Set<number> {
    const completed = normalizeCompletedStages(project.completedStages ?? project.completed_stages);
    const currentStage = Number(project.currentStage ?? project.current_stage ?? 1);
    const formData = (project.formData || project.form_data || {}) as Record<string, unknown>;
    const photos = Array.isArray(project.photos) ? project.photos : [];
    const documents = Array.isArray(project.documents) ? project.documents : [];
    const videos = Array.isArray(project.videos) ? project.videos : [];
    const scope = Array.isArray(project.scope) ? project.scope : [];
    const quoteComplete = projectHasCompletedQuote(project);
    const hasStage1Input = hasText(formData.notes)
        || hasText(formData.address)
        || hasText(formData.jobDescription)
        || photos.length > 0
        || documents.length > 0
        || videos.length > 0;
    const hasSummary = !!(project.projectSummary || project.project_summary);
    const hasRisk = hasRiskAnalysisPayload(project);
    const hasWms = hasStructuredWms(project);
    const hasScope = scope.length > 0;
    // 5B / O-F11. Scope existing is not evidence that Risk or Method happened: the Fast-Track route, the re-quote shortcut, manual
    // scope entry and an Assistant proposal all produce scope without either. Nor is a Method evidence of Risk: "Generate Anyway" writes a
    // real Method with no Risk report. Risk is evidenced by its own report, or by a completed quote on a route that does not skip Risk and
    // Method (a saved or legacy completed quote is reviewed passively); on a route that SKIPS them (Fast-Track, or re-quote provenance) a
    // completed quote is not evidence either. A Stage 1 or Stage 2 flag the project already carries is kept whatever the evidence (legacy
    // projects, including ones the old re-quote saved as [1, 2, 3], keep what they were saved with), so for those two stages the inference
    // only declines to ADD. Stages 3, 4 and 5 keep the evidence filtering below: a saved Method flag without a usable Method is still
    // dropped here, and the live assessment hook still removes a Method flag the Method-readiness check does not accept.
    const routeSkipsRiskAndMethod = skipsRiskAndMethod(project);
    // A Quote Brain quote is not evidence of a Risk report or a Work Method either: the brain writes neither (lucid 017 O1). On its jobs
    // Stage 2 and Stage 3 are done only on their own evidence, and a flag the brain's earlier server write stamped ([1,2,3,4]) is dropped.
    const brainQuote = isQuoteBrainQuote(project);

    if (hasStage1Input && (hasSummary || hasRisk || hasWms || hasScope || quoteComplete || currentStage > 1)) {
        completed.add(1);
    }
    if (hasRisk || (quoteComplete && !routeSkipsRiskAndMethod && !brainQuote)) {
        completed.add(2);
    } else if (brainQuote) {
        completed.delete(2);
    }
    const hasUsableMethod = hasWms && stage3MethodReady(project);
    const quoteOnlyMethod = !hasWms && quoteComplete && !brainQuote;
    if (hasUsableMethod || (quoteOnlyMethod && !routeSkipsRiskAndMethod)) {
        completed.add(3);
    } else if (!quoteOnlyMethod) {
        completed.delete(3);
    }
    // Stage 4 and Stage 5 are peer views. Merely opening the Stage 5 preview
    // must not turn Stage 4 green; keep an explicit persisted Stage 4 receipt
    // when scope still exists, or infer it from a structurally completed quote.
    if (quoteComplete || (hasScope && completed.has(4))) {
        completed.add(4);
    } else {
        // Persisted completion flags are hints, not durable evidence. A
        // cancelled/restarted pipeline can retain completedStages:[4] while
        // no Stage 4 scope exists; leaving it behind renders a false green
        // check upstream of the work that actually completed.
        completed.delete(4);
    }
    // Stage 5 is "done" once a real quote has been generated. quoteData only
    // exists after quote generation (never auto-populated like scope), so this
    // greens Stage 5 whenever a quote exists — regardless of the stage currently
    // being viewed — and it stays green on back-navigation. (Peter 2026-06-18:
    // Stage 5 green = quote generation completed.) The prior
    // `currentStage >= 5 || completed.has(5)` gate dropped the green when the
    // user navigated back to an earlier stage.
    if (quoteComplete) {
        completed.add(5);
    } else {
        // Persisted stage flags are workflow hints, not proof that Stage 5
        // finished writing. Remove stale completion when the generated scope
        // structure is partial so the project can resume quote preparation.
        completed.delete(5);
    }

    return completed;
}
